import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

const noteStrip = cva(
  "flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 rounded-[var(--radius-sm)] border-l-3 px-3 py-2 font-sans text-meta",
  {
    variants: {
      tone: {
        info: "border-l-info bg-info-soft/40 text-ink-2",
        warning: "border-l-warning bg-warning-soft/60 text-ink-2",
        critical: "border-l-critical bg-critical-soft/60 text-ink-2",
      },
    },
    defaultVariants: { tone: "info" },
  },
);

export interface NoteStripProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title">, VariantProps<typeof noteStrip> {
  /** The bold lead word or phrase — "In review." "Server key." */
  lead: ReactNode;
  children?: ReactNode;
}

/**
 * A one-line notice, smaller and quieter than `Callout`: a bold lead followed
 * by the sentence that explains it, for a spot inside a row or a sheet section
 * where a bordered card would be too much furniture for one line.
 */
export function NoteStrip({ tone, lead, children, className, ...props }: NoteStripProps) {
  return (
    <div className={cn(noteStrip({ tone }), className)} {...props}>
      <span className="font-semibold text-ink">{lead}</span>
      {children ? <span>{children}</span> : null}
    </div>
  );
}
